import { useEffect, useRef, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { workerAtom, type WorkerPhase } from "../stores/worker";
import { localeAtom } from "../stores/locale";
import { useT, type MessageKey } from "../lib/i18n";
import { snapshotApi } from "../api/client";

const phaseLabelKeys: Record<string, string> = {
  idle: "",
  queued: "worker.queued",
  cloning: "worker.cloning",
  signaling: "worker.signaling",
  persisting: "worker.persisting",
  completed: "worker.completed",
  failed: "worker.failed",
};

// The observation runs three ordered stages. Per-repo step/total is only
// emitted during cloning, so once signaling starts the step counter and bar go
// away — without a stage indicator the run looks stalled. These dots give
// honest whole-run forward motion (1→2→3) that doesn't pretend to know
// sub-progress we can't measure.
const PHASE_STEPS: { phase: WorkerPhase; labelKey: MessageKey }[] = [
  { phase: "cloning", labelKey: "worker.cloning" },
  { phase: "signaling", labelKey: "worker.signaling" },
  { phase: "persisting", labelKey: "worker.persisting" },
];

// friendlyErrorKey maps the worker's raw error (often raw git stderr — e.g.
// "exit status 128") to a plain-language i18n key. The raw text is still shown
// via title= for debugging, but the user reads a cause, not a Go error string.
// Mirrors ObservationLogPanel.friendlyFailure (message-only — WorkerStatus has
// no per-stage signal here).
function friendlyErrorKey(raw: string): MessageKey {
  const m = (raw || "").toLowerCase();
  if (/not found|404|repository not found|does not exist/.test(m)) return "worker.err.notFound";
  if (/permission|denied|could not read username|authentication failed|403|terminal prompts disabled/.test(m))
    return "worker.err.permission";
  if (/timeout|timed out|deadline exceeded|context canceled|context deadline/.test(m)) return "worker.err.timeout";
  if (/too large|exceeds|size limit/.test(m)) return "worker.err.tooLarge";
  // "exit status 128" and other git failures surface during the clone stage.
  if (/exit status|clone|fatal|git/.test(m)) return "worker.err.clone";
  return "worker.err.generic";
}

// accountLogin lets the widget rehydrate an in-flight run after a reload or in a
// fresh tab. The worker atom is in-memory only, so a backfill triggered earlier
// (or from another tab) leaves this widget idle even while the server is mid-run.
// Layout passes the current galaxy from useParams; callers without a galaxy scope
// (e.g. CareerLayout) omit it and keep the old in-session-only behaviour.
export default function WorkerStatus({ accountLogin }: { accountLogin?: string }) {
  const [worker, setWorker] = useAtom(workerAtom);
  const [elapsed, setElapsed] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  // Tracks whether the current run was ever observed in flight — gates the
  // org-scan idle→completed transition (see the poll effect).
  const sawInFlightRef = useRef(false);
  const locale = useAtomValue(localeAtom);
  const t = useT(locale);

  // Rehydrate: on mount / account change, if we're idle but the server reports a
  // run in flight, flip active so the poll loop below resumes. Without this a
  // multi-hour backfill (e.g. full-history timeline re-observation) showed nothing
  // after a reload even though it was still running.
  useEffect(() => {
    if (worker.active || !accountLogin) return;
    let cancelled = false;
    snapshotApi
      .progress(accountLogin)
      .catch(() => null)
      .then((p) => {
        if (cancelled || !p || !p.phase) return;
        const inFlight =
          p.phase === "queued" ||
          p.phase === "cloning" ||
          p.phase === "signaling" ||
          p.phase === "persisting";
        if (!inFlight) return;
        setWorker((prev) => ({
          ...prev,
          active: true,
          org: accountLogin,
          phase: p.phase as WorkerPhase,
          detail: p.detail || "",
          step: p.step || 0,
          total: p.total || 0,
          startedAt: p.started_at ? new Date(p.started_at).getTime() : prev.startedAt,
          error: p.error || "",
        }));
      });
    return () => {
      cancelled = true;
    };
  }, [accountLogin, worker.active, setWorker]);

  // Poll worker progress when active
  useEffect(() => {
    if (!worker.active || !worker.org) return;

    // Reset per run: the org (SFN) scan has no "completed" signal — it stops
    // being reported and the endpoint falls back to "idle". We only treat that
    // idle as completion once we've actually SEEN the scan in flight, so the
    // brief idle before the execution is first listed can't complete it early.
    sawInFlightRef.current = false;

    const interval = setInterval(async () => {
      try {
        const progress = await snapshotApi.progress(worker.org).catch(() => null);
        const inFlight =
          progress?.phase === "queued" ||
          progress?.phase === "cloning" ||
          progress?.phase === "signaling" ||
          progress?.phase === "persisting";
        if (inFlight) sawInFlightRef.current = true;

        if (progress && progress.phase && progress.phase !== "idle") {
          setWorker((prev) => ({
            ...prev,
            phase: progress.phase as WorkerPhase,
            detail: progress.detail || "",
            step: progress.step || 0,
            total: progress.total || 0,
            startedAt: progress.started_at
              ? new Date(progress.started_at).getTime()
              : prev.startedAt,
            error: progress.error || "",
          }));
        }

        if (progress?.phase === "completed") {
          setWorker((prev) => ({ ...prev, phase: "completed", active: false }));
          setDismissed(false);
        } else if (progress?.phase === "idle" && sawInFlightRef.current) {
          // The org scan finished (no longer a RUNNING execution). Close the
          // loop as completed so the widget clears instead of lingering on the
          // last in-flight phase and needing a page reload.
          setWorker((prev) => ({ ...prev, phase: "completed", active: false }));
          setDismissed(false);
        }

        // phase="failed" is terminal — stop the polling loop by flipping
        // active=false, then let the user read the error and dismiss.
        // Before this the loop kept polling forever on "waiting" because
        // the worker never transitioned to "completed".
        if (progress?.phase === "failed") {
          setWorker((prev) => ({
            ...prev,
            phase: "failed",
            active: false,
            error: progress.error || "unknown error",
          }));
          setDismissed(false);
        }
      } catch {
        // retry silently
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [worker.active, worker.org, setWorker]);

  // Elapsed timer — ticks every second so ETA updates smoothly.
  useEffect(() => {
    if (!worker.active || !worker.startedAt) return;
    const tick = () => setElapsed(Math.floor((Date.now() - worker.startedAt!) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [worker.active, worker.startedAt]);

  // Auto-dismiss completed after 10s
  useEffect(() => {
    if (worker.phase === "completed" && !worker.active) {
      const timeout = setTimeout(() => setDismissed(true), 10000);
      return () => clearTimeout(timeout);
    }
  }, [worker.phase, worker.active]);

  if (worker.phase === "idle" || (dismissed && !worker.active)) return null;

  const formatDuration = (s: number) => {
    if (s < 0 || !isFinite(s)) return "—";
    const m = Math.floor(s / 60);
    const sec = Math.max(0, s % 60);
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  const progressPct = worker.total > 0 ? (worker.step / worker.total) * 100 : undefined;
  const labelKey = phaseLabelKeys[worker.phase];
  const label = labelKey ? t(labelKey as MessageKey) : worker.phase;

  // Index of the current stage in PHASE_STEPS (cloning=0 … persisting=2);
  // -1 while queued. "completed" sits past the last stage so all dots fill.
  const currentStageIdx =
    worker.phase === "completed"
      ? PHASE_STEPS.length
      : PHASE_STEPS.findIndex((s) => s.phase === worker.phase);

  // ETA: extrapolate remaining seconds from elapsed / step against total.
  // Only valid once we've observed at least one completed step.
  const etaSeconds =
    worker.active && worker.step > 0 && worker.total > worker.step && elapsed > 0
      ? Math.round((elapsed / worker.step) * (worker.total - worker.step))
      : null;

  const isFailed = worker.phase === "failed";
  const isCompleted = worker.phase === "completed";

  return (
    <div
      className="px-lg py-md transition-all"
      style={{
        borderBottom: "1px solid var(--border)",
        background: "var(--surface-sunken)",
        fontFamily: "var(--mono)",
        fontSize: "11px",
        letterSpacing: "0.08em",
      }}
    >
      <div className="flex items-center gap-md">
        {/* Status dot */}
        {worker.active ? (
          <span className="relative flex h-2 w-2">
            <span
              className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
              style={{ backgroundColor: "var(--gold)" }}
            />
            <span
              className="relative inline-flex h-2 w-2 rounded-full"
              style={{ backgroundColor: "var(--gold)" }}
            />
          </span>
        ) : isCompleted ? (
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: "#34d399" }} />
        ) : (
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: "#f87171" }} />
        )}

        {/* Phase label */}
        <span
          className="uppercase"
          style={{
            color: isFailed ? "#f87171" : isCompleted ? "#34d399" : "var(--text-strong)",
            fontWeight: 500,
          }}
        >
          {label}
        </span>

        {/* Stage stepper (cloning → signaling → persisting). Carries forward
            motion through signaling/persisting, where per-repo step isn't
            emitted. Only shown while a run is in flight. */}
        {worker.active && currentStageIdx >= 0 && (
          <span className="flex items-center gap-xs">
            {PHASE_STEPS.map((s, i) => (
              <span
                key={s.phase}
                className="h-1.5 w-1.5 rounded-full"
                title={t(s.labelKey)}
                style={{
                  backgroundColor: i <= currentStageIdx ? "var(--gold)" : "var(--border)",
                }}
              />
            ))}
          </span>
        )}

        {/* Detail */}
        {worker.detail && (
          <span style={{ color: "var(--text-muted)" }}>{worker.detail}</span>
        )}

        {/* Error — only shown on failure. The raw worker error (often raw git
            stderr like "exit status 128") is mapped to a friendly cause; the raw
            text is kept on title= for debugging. Truncated so a long message
            doesn't push the dismiss button off-screen. */}
        {isFailed && worker.error && (
          <span
            className="truncate"
            style={{ color: "#f87171", maxWidth: "40ch" }}
            title={worker.error}
          >
            {t(friendlyErrorKey(worker.error))}
          </span>
        )}

        {/* Progress bar */}
        {progressPct !== undefined && worker.active && (
          <div
            className="w-32"
            style={{ height: "1px", backgroundColor: "var(--border)" }}
          >
            <div
              className="transition-all duration-500"
              style={{
                width: `${progressPct}%`,
                height: "1px",
                backgroundColor: "var(--gold)",
              }}
            />
          </div>
        )}

        {/* Step counter */}
        {worker.step > 0 && worker.total > 0 && (
          <span className="tabular-nums" style={{ color: "var(--text-dim)" }}>
            {worker.step}/{worker.total}
          </span>
        )}

        {/* Elapsed + ETA */}
        {worker.active && (
          <span className="ml-auto flex items-center gap-md tabular-nums">
            <span style={{ color: "var(--text-dim)" }}>
              {formatDuration(elapsed)}
            </span>
            {etaSeconds !== null && (
              <span
                style={{ color: "var(--gold)" }}
                title={t("worker.etaTooltip")}
              >
                {t("worker.etaApprox")}
                {formatDuration(etaSeconds)}
              </span>
            )}
          </span>
        )}

        {/* Dismiss */}
        {!worker.active && (
          <button
            onClick={() => {
              setDismissed(true);
              setWorker({
                active: false,
                phase: "idle",
                detail: "",
                step: 0,
                total: 0,
                startedAt: null,
                org: "",
                error: "",
              });
            }}
            className="ml-auto transition-colors"
            style={{ color: "var(--text-dim)" }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text-strong)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-dim)"; }}
          >
            &#x2715;
          </button>
        )}
      </div>
    </div>
  );
}
